import axios from 'axios';

import { formatAddress } from '../address-helpers';
import { createCancelablePromise } from '../../../../services/promises/create-cancelable-promise';

const API_BASE_URL = '/purchaseOrders/suppliers';

const formatSupplierResponse = (supplier) => {
  const address = formatAddress({
    line1: supplier.addressLine1,
    line2: supplier.addressLine2,
    city: supplier.city,
    state: supplier.state,
    postalCode: supplier.postalCode,
    countryCode: supplier.country,
  });

  return {
    supplierId: supplier.supplierId,
    key: supplier.supplierId.toString(),
    name: address ? `[${supplier.supplierName}] - ${address}` : `[${supplier.supplierName}]`,
  };
};

export const supplierApi = {
  getAllSuppliers: async () => {
    const response = await axios.get(API_BASE_URL);
    return response.data.suppliers.map(formatSupplierResponse);
  },

  /**
   * Fetches suppliers with filtering, sorting, and pagination support
   * @param {object} filterRequest - The filter/sort/pagination request
   * @param {object} [filterRequest.filter] - Filter configuration
   * @param {Array<{column: string, isIn: boolean, values: string[]}>} [filterRequest.filter.collectionFilters] - Collection filters
   * @param {string} [filterRequest.orderBy] - Column to order by
   * @param {'ascending'|'descending'} [filterRequest.direction] - Sort direction
   * @param {object} [filterRequest.page] - Pagination configuration
   * @param {number} [filterRequest.page.pageNumber] - Page number
   * @param {number} [filterRequest.page.pageSize] - Page size
   * @returns {{promise: Promise<{suppliers: Array<object>, totalCount: number}>, cancel: () => void}} Cancelable promise with suppliers and total count
   */

  getAllSuppliersGrid: (filterRequest = {}) => {
    const requestPromise = axios.post(`${API_BASE_URL}/grid`, filterRequest).then((response) => ({
      suppliers: response.data.suppliers,
      totalCount: response.data.totalCount ?? 0,
    }));

    return createCancelablePromise(requestPromise);
  },

  getSupplier: async (supplierId) => {
    const response = await axios.get(`${API_BASE_URL}/${supplierId}`);
    return response.data;
  },

  createSupplier: async (supplierData) => {
    const response = await axios.post(API_BASE_URL, supplierData);
    return response.data;
  },

  updateSupplier: async (supplierId, supplierData) => {
    const response = await axios.put(`${API_BASE_URL}/${supplierId}`, supplierData);
    return response.data;
  },
};
